import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Package, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button';
import DataGridV2 from '@components/atoms/datatable/data-grid-v2';
import { useGetAdminPlans } from '@utils/api/admin/use-get-admin-plans';
import { useDeleteAdminPlan } from '@utils/api/admin/use-delete-admin-plan';
import { AdminPlan } from '@utils/types/admin';
import AdminPlanFormModal from './plan-form-modal';
import PlanDeleteModal from './plan-delete-modal';
import { getPlanColumns } from './plan-table-columns';

export default function AdminPlans() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { data, isLoading, isError } = useGetAdminPlans();
  const deleteMutation = useDeleteAdminPlan();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<AdminPlan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<AdminPlan | null>(null);

  const plans = data?.result ?? [];

  const handleCreate = () => {
    setEditingPlan(null);
    setIsModalOpen(true);
  };

  const handleEdit = (plan: AdminPlan) => {
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (planToDelete) {
      deleteMutation.mutate(planToDelete.id, {
        onSuccess: () => {
          toast.success(t('plan-deleted'));
          setPlanToDelete(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      });
    }
  };

  const columns = useMemo(
    () =>
      getPlanColumns({
        t,
        getColour,
        isDeleting: deleteMutation.isLoading,
        onEdit: handleEdit,
        onDelete: setPlanToDelete
      }),
    [t, getColour, deleteMutation.isLoading]
  );

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <PageHeader
        title={t('plans')}
        caption={t('manage-plans-for-on-prem-deployment')}
      >
        <Button
          onClick={handleCreate}
          variant="primary"
          size="small"
          icon={<Plus className="h-4 w-4" />}
          label={t('create-plan')}
        />
      </PageHeader>

      {isError && (
        <p className="text-sm text-red-500 dark:text-red-400 mb-4">
          {t('an-error-occurred')}
        </p>
      )}

      {!isLoading && plans.length === 0 ? (
        <div
          className="rounded-2xl border p-8 text-center justify-end"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <Package
            className="h-12 w-12 mx-auto mb-4"
            style={{ color: getColour('inactiveText') }}
          />
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {t('no-plans-available')}
          </p>
          <Button
            onClick={handleCreate}
            variant="primary"
            size="small"
            icon={<Plus className="h-4 w-4" />}
            label={t('create-first-plan')}
            className="mt-4"
          />
        </div>
      ) : (
        <div
          className="rounded-2xl border overflow-hidden"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <DataGridV2
            data={plans}
            columns={columns}
            loading={isLoading}
            enableSorting
            enableFiltering
          />
        </div>
      )}

      {isModalOpen && (
        <AdminPlanFormModal
          plan={editingPlan}
          onClose={() => {
            setIsModalOpen(false);
            setEditingPlan(null);
          }}
        />
      )}

      {planToDelete && (
        <PlanDeleteModal
          plan={planToDelete}
          isLoading={deleteMutation.isLoading}
          onConfirm={handleConfirmDelete}
          onCancel={() => setPlanToDelete(null)}
        />
      )}
    </div>
  );
}
